import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { apiGet, apiPost, refreshSession, setAccessToken } from "./api";
import type { ConsoleUser } from "./types";

interface LoginArgs {
  shopSlug: string;
  email: string;
  password: string;
}

interface AuthState {
  user: ConsoleUser | null;
  loading: boolean;
  login: (a: LoginArgs) => Promise<void>;
  logout: () => Promise<void>;
  setSession: (user: ConsoleUser) => void;
}

const AuthCtx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

interface SessionResponse {
  tokens: { access_token: string };
  user: ConsoleUser;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ConsoleUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Hydrate from the refresh cookie on load — the access token is memory-only,
  // so a page reload re-mints it from the httpOnly refresh cookie, then /me.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await refreshSession();
      if (ok && !cancelled) {
        try {
          const data = await apiGet<{ user: ConsoleUser }>("/auth/me");
          if (!cancelled) setUser(data.user);
        } catch {
          /* not signed in */
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async ({ shopSlug, email, password }: LoginArgs) => {
    const data = await apiPost<SessionResponse>("/auth/login", {
      shop_slug: shopSlug,
      email,
      password,
    });
    setAccessToken(data.tokens.access_token);
    setUser(data.user);
  };

  const logout = async () => {
    try {
      await apiPost("/auth/logout");
    } catch {
      /* best effort */
    }
    setAccessToken(null);
    setUser(null);
  };

  const setSession = (u: ConsoleUser) => setUser(u);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, setSession }}>
      {children}
    </AuthCtx.Provider>
  );
}
